'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, FileText, Hash, MessageSquare, Type, FileInput } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { sentenceCounterContent } from '../../data/sentence-counter-content';
import Link from 'next/link';

type CountResult = {
    sentences: number;
    words: number;
    characters: number;
    charactersNoSpaces: number;
    avgWordsPerSentence: number;
    avgWordLength: number;
};

export default function SentenceCounter() {
    const [text, setText] = useState('');
    const [countResult, setCountResult] = useState<CountResult>({
        sentences: 0,
        words: 0,
        characters: 0,
        charactersNoSpaces: 0,
        avgWordsPerSentence: 0,
        avgWordLength: 0
    });
    const [copied, setCopied] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const { toast } = useToast();

    const calculateCounts = useCallback((text: string) => {
        if (!text.trim()) {
            return {
                sentences: 0,
                words: 0,
                characters: 0,
                charactersNoSpaces: 0,
                avgWordsPerSentence: 0,
                avgWordLength: 0
            };
        }

        // Basic counts
        const characters = text.length;
        const charactersNoSpaces = text.replace(/\s+/g, '').length;

        // Sentence splitting - handles common sentence endings and multiple spaces
        const sentenceRegex = /[.!?]+\s*|\n\s*\n/g;
        const sentences = text.split(sentenceRegex).filter(s => s.trim().length > 0);

        // Word splitting - handles multiple spaces and newlines
        const words = text.trim() === '' ? [] : text.trim().split(/\s+/);

        // Calculate averages
        const sentenceCount = sentences.length;
        const wordCount = words.length;
        const avgWordsPerSentence = sentenceCount > 0 ? Number((wordCount / sentenceCount).toFixed(1)) : 0;
        const totalWordLength = words.reduce((sum, word) => sum + word.length, 0);
        const avgWordLength = wordCount > 0 ? Number((totalWordLength / wordCount).toFixed(1)) : 0;

        return {
            sentences: sentenceCount,
            words: wordCount,
            characters,
            charactersNoSpaces,
            avgWordsPerSentence,
            avgWordLength
        };
    }, []);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        setText(newText);
        setCountResult(calculateCounts(newText));
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setText(fileContent);
            setCountResult(calculateCounts(fileContent));
        };
        reader.readAsText(file);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => {
        setText('');
        setCountResult({
            sentences: 0,
            words: 0,
            characters: 0,
            charactersNoSpaces: 0,
            avgWordsPerSentence: 0,
            avgWordLength: 0
        });
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Sentence Counter">
                Count sentences in text with our free online tool. Analyze sentence structure, count words per sentence, and improve writing readability.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <div>
                                    <CardTitle>Text Input</CardTitle>
                                    <CardDescription>Type, paste, or upload text to analyze sentences</CardDescription>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        className="text-xs"
                                    >
                                        {showAdvanced ? 'Hide Details' : 'Show Details'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={text}
                                    onChange={handleTextChange}
                                    placeholder="Type or paste your text here..."
                                    className="min-h-[300px] w-full text-base"
                                    rows={10}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Copy to clipboard"
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!text}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center">
                                <div className="relative">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        asChild
                                        className="cursor-pointer"
                                    >
                                        <Label htmlFor="file-upload" className="cursor-pointer">
                                            <FileInput className="h-4 w-4 mr-2" />
                                            Upload File
                                            <input
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                accept=".txt,.md,.doc,.docx,.pdf"
                                            />
                                        </Label>
                                    </Button>
                                </div>
                                <div className="ml-4 text-sm text-muted-foreground">
                                    Supports: .txt, .md, .doc, .docx, .pdf
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Text Analysis</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="text-center">
                                <div className="text-5xl font-bold">{countResult.sentences.toLocaleString()}</div>
                                <div className="text-muted-foreground">Sentences</div>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <StatCard
                                        icon={<Type className="h-4 w-4" />}
                                        label="Words"
                                        value={countResult.words.toLocaleString()}
                                    />
                                    <StatCard
                                        icon={<Hash className="h-4 w-4" />}
                                        label="Characters"
                                        value={countResult.characters.toLocaleString()}
                                    />
                                </div>

                                {showAdvanced && (
                                    <div className="border-t pt-4 mt-4">
                                        <h3 className="font-medium mb-3">Advanced Metrics</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Characters (no spaces):</span>
                                                <span className="font-medium">{countResult.charactersNoSpaces.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Avg. words per sentence:</span>
                                                <span className="font-medium">{countResult.avgWordsPerSentence}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">Avg. word length:</span>
                                                <span className="font-medium">{countResult.avgWordLength} chars</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>About Sentence Counter</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                This tool helps you analyze text by counting sentences, words, and characters, along with providing reading and writing metrics.
                            </p>
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">How it works:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Sentences are split at periods, question marks, exclamation marks, and paragraph breaks</li>
                                    <li>Words are sequences of characters separated by whitespace</li>
                                    <li>Character count includes all visible and whitespace characters</li>
                                    <li>Averages are calculated based on the total counts</li>
                                </ul>
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">Common uses:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Analyzing writing style and readability</li>
                                    <li>Meeting word or sentence count requirements</li>
                                    <li>Improving writing conciseness</li>
                                    <li>Academic writing and content creation</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={sentenceCounterContent.title}
                description={sentenceCounterContent.description}
                content={sentenceCounterContent.content}
                faqs={sentenceCounterContent.faqs}
                relatedTools={sentenceCounterContent.relatedTools}
            />
        </div>
    );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
    return (
        <div className="border rounded-lg p-3">
            <div className="flex items-center space-x-2 text-muted-foreground">
                {icon}
                <span className="text-sm">{label}</span>
            </div>
            <p className="mt-1 text-xl font-semibold">{value}</p>
        </div>
    );
}
